import React, { useState } from 'react';
import { useStore } from '../../stores/useStore';
import { Badge } from '../Common/Badge';
import { EmptyState } from '../Common/EmptyState';
import { ClientDetail } from './ClientDetail';
import { Client, ClientType, Priority } from '../../types';

const CLIENT_TYPES: ClientType[] = ['업체', '자재'];
const PRIORITIES: Priority[] = ['일반', 'VIP', '주의'];

const labelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 700,
    color: '#5a5a5a',
    marginBottom: '4px',
    display: 'block',
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'white',
    border: '1px solid #c4b8a5',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: "'Noto Sans KR', sans-serif",
    boxSizing: 'border-box',
    color: '#2c2c2c',
};

const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%235a5a5a' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '16px',
    paddingRight: '28px',
};

interface AddForm {
    name: string;
    type: ClientType;
    phone: string;
    email: string;
    address: string;
    managerName: string;
    managerRole: string;
    managerPhone: string;
    notes: string;
    priority: Priority;
}

const defaultForm = (): AddForm => ({
    name: '',
    type: '업체',
    phone: '',
    email: '',
    address: '',
    managerName: '',
    managerRole: '',
    managerPhone: '',
    notes: '',
    priority: '일반',
});

const priorityColors = {
    'VIP': 'yellow',
    '일반': 'gray',
    '주의': 'red',
} as const;

export const ClientList: React.FC = () => {
    const { clients, machines, addClient, toggleClientFavorite, favoriteClientIds } = useStore();
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [form, setForm] = useState<AddForm>(defaultForm());
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

    const selectedClient = selectedClientId ? clients.find((c) => c.id === selectedClientId) ?? null : null;

    if (selectedClient) {
        const clientMachines = machines.filter((m) => m.clientId === selectedClient.id);
        return (
            <ClientDetail
                client={selectedClient}
                machineCount={clientMachines.length}
                onBack={() => setSelectedClientId(null)}
            />
        );
    }

    const filtered = clients
        .filter((c) => c.name.includes(searchQuery))
        .sort((a, b) => {
            const aFav = favoriteClientIds.includes(a.id);
            const bFav = favoriteClientIds.includes(b.id);
            if (aFav !== bFav) return aFav ? -1 : 1;
            return a.name.localeCompare(b.name, 'ko');
        });

    const totalOutstanding = clients.reduce((sum, c) => sum + c.outstandingAmount, 0);

    const handleSubmit = () => {
        if (!form.name.trim()) return;

        const managers = form.managerName.trim()
            ? [{
                name: form.managerName.trim(),
                role: form.managerRole.trim() || '담당자',
                phone: form.managerPhone.trim(),
                email: '',
            }]
            : [];

        const newClient: Client = {
            id: `c_${Date.now()}`,
            name: form.name.trim(),
            type: form.type,
            contact: {
                phone: form.phone.trim(),
                email: form.email.trim(),
                address: form.address.trim(),
            },
            managers,
            machines: [],
            transactionHistory: [],
            outstandingAmount: 0,
            notes: form.notes.trim(),
            priority: form.priority,
        };

        addClient(newClient);
        setForm(defaultForm());
        setShowAddModal(false);
    };

    return (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* 검색 + 추가 버튼 */}
            <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#a09890' }}>🔍</span>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="거래처명 검색"
                        style={{
                            width: '100%',
                            padding: '11px 12px 11px 36px',
                            border: '1px solid #c4b8a5',
                            borderRadius: '10px',
                            fontSize: '14px',
                            outline: 'none',
                            fontFamily: "'Noto Sans KR', sans-serif",
                            backgroundColor: 'white',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>
                <button
                    onClick={() => { setForm(defaultForm()); setShowAddModal(true); }}
                    style={{
                        padding: '11px 16px',
                        backgroundColor: '#1e3a5f',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    + 추가
                </button>
            </div>

            {/* 미수금 알림 */}
            {totalOutstanding > 0 && (
                <div style={{ backgroundColor: '#ffebee', border: '1px solid #c75c2e', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>⚠️</span>
                    <div>
                        <p style={{ fontSize: '14px', fontWeight: 700, color: '#c75c2e' }}>
                            미수금 총 {totalOutstanding.toLocaleString()}원
                        </p>
                        <p style={{ fontSize: '12px', color: '#8b4513' }}>
                            {clients.filter((c) => c.outstandingAmount > 0).length}개 거래처
                        </p>
                    </div>
                </div>
            )}

            {/* 거래처 목록 */}
            {filtered.length === 0 ? (
                <EmptyState
                    message={searchQuery ? '검색 결과가 없습니다' : '등록된 거래처가 없습니다'}
                    subMessage={searchQuery ? '' : '+ 거래처 버튼으로 추가해보세요'}
                />
            ) : (
                <div style={{ border: '1px solid #c4b8a5', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden' }}>
                    {filtered.map((client, index) => {
                        const clientMachines = machines.filter((m) => m.clientId === client.id);
                        const isLast = index === filtered.length - 1;
                        return (
                            <div
                                key={client.id}
                                onClick={() => setSelectedClientId(client.id)}
                                style={{
                                    padding: '16px',
                                    borderBottom: isLast ? 'none' : '1px solid #e8e0d5',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#faf8f5'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <h3 style={{ fontWeight: 700, color: '#2c2c2c', fontSize: '16px' }}>{client.name}</h3>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleClientFavorite(client.id); }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0 2px', lineHeight: 1, flexShrink: 0, color: favoriteClientIds.includes(client.id) ? '#e8a020' : '#c4b8a5' }}
                                            >
                                                {favoriteClientIds.includes(client.id) ? '★' : '☆'}
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'center' }}>
                                            <Badge color={priorityColors[client.priority]} size="sm">
                                                {client.priority}
                                            </Badge>
                                            <span style={{ fontSize: '11px', color: '#a09890' }}>{client.type}</span>
                                        </div>
                                    </div>
                                    {client.outstandingAmount > 0 && (
                                        <p style={{ fontSize: '14px', fontWeight: 700, color: '#c75c2e', flexShrink: 0 }}>
                                            미수 {client.outstandingAmount.toLocaleString()}원
                                        </p>
                                    )}
                                </div>

                                <div style={{ fontSize: '13px', color: '#666', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {client.contact.phone && <p>📞 {client.contact.phone}</p>}
                                    {client.contact.address && <p>📍 {client.contact.address}</p>}
                                </div>

                                {client.managers.length > 0 && (
                                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {client.managers.map((m, i) => (
                                            <span key={i} style={{ fontSize: '11px', backgroundColor: '#e3f2fd', color: '#1e3a5f', padding: '3px 8px', borderRadius: '20px', fontWeight: 700 }}>
                                                {m.name} {m.role}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {(client.notes || clientMachines.length > 0) && (
                                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e8e0d5', fontSize: '12px', color: '#a09890', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>기계 {clientMachines.length}대</span>
                                        {client.notes && <span>{client.notes}</span>}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 거래처 추가 모달 */}
            {showAddModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                    onClick={() => setShowAddModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#f4f1ea',
                            borderTop: '2px solid #c75c2e',
                            borderRadius: '20px 20px 0 0',
                            width: '100%',
                            maxHeight: '88vh',
                            overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            backgroundColor: '#1e3a5f',
                            color: 'white',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <span style={{ fontWeight: 700, fontSize: '15px' }}>거래처 추가</span>
                            <button
                                onClick={() => setShowAddModal(false)}
                                style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={labelStyle}>거래처명 *</label>
                                <input
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="예: 강남 빌딩"
                                    style={inputStyle}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>유형</label>
                                    <select
                                        value={form.type}
                                        onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as ClientType }))}
                                        style={selectStyle}
                                    >
                                        {CLIENT_TYPES.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>우선순위</label>
                                    <select
                                        value={form.priority}
                                        onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as Priority }))}
                                        style={selectStyle}
                                    >
                                        {PRIORITIES.map((p) => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label style={labelStyle}>전화번호</label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                    placeholder="예: 02-1234-5678"
                                    style={inputStyle}
                                />
                            </div>

                            <div>
                                <label style={labelStyle}>주소</label>
                                <input
                                    value={form.address}
                                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                                    placeholder="예: 서울특별시 강남구 ..."
                                    style={inputStyle}
                                />
                            </div>

                            {/* 담당자 */}
                            <div style={{
                                border: '1px solid #c4b8a5',
                                borderRadius: '12px',
                                padding: '12px',
                                backgroundColor: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                            }}>
                                <p style={{ fontSize: '12px', fontWeight: 700, color: '#5a5a5a' }}>담당자 (선택사항)</p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>이름</label>
                                        <input
                                            value={form.managerName}
                                            onChange={(e) => setForm((f) => ({ ...f, managerName: e.target.value }))}
                                            placeholder="홍길동"
                                            style={inputStyle}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>직책</label>
                                        <input
                                            value={form.managerRole}
                                            onChange={(e) => setForm((f) => ({ ...f, managerRole: e.target.value }))}
                                            placeholder="시설팀장"
                                            style={inputStyle}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label style={labelStyle}>연락처</label>
                                    <input
                                        type="tel"
                                        value={form.managerPhone}
                                        onChange={(e) => setForm((f) => ({ ...f, managerPhone: e.target.value }))}
                                        placeholder="010-0000-0000"
                                        style={inputStyle}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={labelStyle}>메모</label>
                                <textarea
                                    value={form.notes}
                                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                    placeholder="특이사항, 결제 조건 등"
                                    style={{ ...inputStyle, height: '72px', resize: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '8px', paddingTop: '4px', paddingBottom: '8px' }}>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: '13px',
                                        border: '1px solid #c4b8a5',
                                        backgroundColor: 'white',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        borderRadius: '10px',
                                        color: '#5a5a5a',
                                    }}
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={!form.name.trim()}
                                    style={{
                                        flex: 2,
                                        padding: '13px',
                                        border: 'none',
                                        backgroundColor: form.name.trim() ? '#c75c2e' : '#c4b8a5',
                                        color: 'white',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        cursor: form.name.trim() ? 'pointer' : 'not-allowed',
                                        borderRadius: '10px',
                                    }}
                                >
                                    거래처 추가
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
